import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

type StructuredData = Record<string, unknown>;

const FALLBACK_SITE_URL =
  import.meta.env.VITE_SITE_URL ??
  (typeof window !== 'undefined' && window.location.origin ? window.location.origin : 'https://advoltix.com');

const SITE_URL = (FALLBACK_SITE_URL ?? 'https://advoltix.com').replace(/\/$/, '');
const SITE_NAME = 'Advoltix';
const DEFAULT_TITLE = 'Advoltix – Intelligent Growth Command Center';
const DEFAULT_DESCRIPTION =
  'Advoltix unifies paid media, automation, and personalization so agencies and brands can orchestrate global growth with confidence.';
const DEFAULT_IMAGE_PATH = '/advoltix-favicon.png';

const resolveAssetUrl = (path?: string) => {
  if (!path) {
    return `${SITE_URL}${DEFAULT_IMAGE_PATH}`;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

export type SeoHeadProps = {
  title?: string;
  description?: string;
  image?: string;
  canonical?: string;
  locale?: string;
  keywords?: string[];
  noindex?: boolean;
  structuredData?: StructuredData | StructuredData[];
  additionalMeta?: Array<{ name?: string; property?: string; content: string }>;
  ogType?: 'website' | 'article' | 'product' | 'profile';
};

export function SeoHead({
  title,
  description,
  image,
  canonical,
  locale = 'en-US',
  keywords,
  noindex,
  structuredData,
  additionalMeta,
  ogType = 'website',
}: SeoHeadProps) {
  const location = useLocation();

  const canonicalUrl = useMemo(() => {
    if (canonical) {
      return canonical;
    }
    return `${SITE_URL}${location.pathname}${location.search}`;
  }, [canonical, location.pathname, location.search]);

  const resolvedTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const resolvedDescription = description ?? DEFAULT_DESCRIPTION;
  const resolvedImage = resolveAssetUrl(image);
  const robotsValue = noindex ? 'noindex, nofollow' : 'index, follow';
  const keywordValue = keywords?.join(', ');
  const structuredDataList = Array.isArray(structuredData)
    ? structuredData
    : structuredData
      ? [structuredData]
      : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang={locale.split('-')[0]} />
      <title>{resolvedTitle}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={robotsValue} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale.replace('-', '_')} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:secure_url" content={resolvedImage} />
      <meta property="og:image:alt" content={resolvedTitle} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />
      <meta name="twitter:site" content="@advoltix" />
      {keywordValue ? <meta name="keywords" content={keywordValue} /> : null}
      {additionalMeta?.map(meta =>
        meta.name ? (
          <meta key={`${meta.name}-${meta.content}`} name={meta.name} content={meta.content} />
        ) : meta.property ? (
          <meta key={`${meta.property}-${meta.content}`} property={meta.property} content={meta.content} />
        ) : null
      )}
      {structuredDataList.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

